import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import withApi from '../../components/higherOrderComponents/withApi';
import { readQuery } from '../query';
import ListTools from '../components/ListTools';
import ActiveFilters from '../components/ActiveFilters';
import EmptyRow from '../components/EmptyRow';
import Guid from '../components/Guid';
import Pager from '../components/Pager';
import LookupSelect from '../components/LookupSelect';
import { lookupBookableProduct, lookupFacility, lookupMembership } from '../lookups';
import { mappedWeekDays, weekdaysReverse } from '../../enums/weekdays';
import { formatCopenhagen } from '../../common/copenhagenTime';

/**
 * Seasonal courts — the searchable list.
 *
 * All six filters of the old page, which the design draws as five (it has no
 * Membership). Facility, membership and product are lookups, as they are there:
 * the two product lookups differ only by the type they ask for — 2 is a
 * membership, 1 is a product — which is why they are two filters and not one.
 *
 * `WeekDay` travels as a name (`Monday`) and `searchCourts` maps it to the number
 * the API wants. That mapping lives in the api module, so the URL stays readable.
 *
 * One trap worth naming: `searchCourts` does
 * `Object.assign(filters, { WeekDay })` — it *mutates* the object it is handed.
 * The query object here is memoised and shared with the filter form, so it is
 * copied on the way in; without that, one search writes `WeekDay: null` into the
 * page's own state.
 */

// Season dates arrive as UTC instants (a season starting 10 Aug is 9 Aug 22:00Z); the Copenhagen day is the one meant.
const fmtDay = value => formatCopenhagen(value, { dateOnly: true, fallback: '—' });
const fmtTime = value => (value ? String(value).split(':').slice(0, 2).join(':') : '—');

const EXPORT_COLUMNS = [
  ['Id', 'Id'],
  ['CourtName', 'Court'],
  ['FacilityName', 'Facility'],
  ['FacilityId', 'Facility id'],
  ['WeekDay', 'Week day'],
  ['StartTime', 'Start'],
  ['EndTime', 'End'],
  ['ProductName', 'Product'],
  ['MembershipName', 'Membership']
];

const FILTER_FIELDS = [
  { name: 'id', label: 'Id' },
  { name: 'CourtName', label: 'Court name' },
  { name: 'WeekDay', label: 'Week day' },
  { name: 'FacilityId', label: 'Facility' },
  { name: 'MembershipId', label: 'Membership' },
  { name: 'ProductId', label: 'Product' }
];

const SeasonalCourtsInternal = ({ api }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => readQuery(location.search), [location.search]);

  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState(query);

  const page = parseInt(query.page, 10) || 1;
  // The same question the chips answer, so the empty row and the chips
  // can never disagree about whether anything is filtering the list.
  const filtered = FILTER_FIELDS.some(({ name: key }) => (
    key !== 'page' && key !== 'resultsPerPage' && query[key] !== undefined && query[key] !== ''
  ));

  const perPage = parseInt(query.resultsPerPage, 10) || 15;

  useEffect(() => {
    setDraft(query);
  }, [query]);

  const load = useCallback(async () => {
    setRows(null);
    setFailed(false);
    try {
      // Copied, not passed: see the note above.
      const data = await api.seasonalcourts.searchCourts({
        filters: { ...query },
        skipPages: page,
        resultsCount: perPage
      });
      setRows(Array.isArray(data) ? data : (data?.Courts ?? []));
    } catch (error) {
      setFailed(true);
    }
  }, [api, query, page]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = (next, { resetPage = true } = {}) => {
    const merged = { ...next };
    if (resetPage) delete merged.page;

    const search = Object.entries(merged)
      .filter(([, value]) => value !== '' && value != null)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    navigate(search ? `?${search}` : '', { replace: false });
  };

  const set = (name, value) => setDraft(previous => ({ ...previous, [name]: value }));

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header next-page__header--row">
        <div>
          <h1>Seasonal courts</h1>
          <p className="next-muted">
            {rows === null
              ? 'Loading…'
              : `Page ${page} · ${rows.length} row${rows.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="next-page__actions">
          <button type="button" className="next-btn" onClick={load}>Refresh</button>
        </div>
      </header>

      <section className="next-card">
        <form
          className="next-filters"
          onSubmit={event => { event.preventDefault(); apply(draft); }}
        >
          <div className="next-filters__row next-filters__row--grid">
            <label className="next-filter">
              <span className="next-filter__label">Id</span>
              <input
                className="next-input"
                value={draft.id ?? ''}
                placeholder="Find by id"
                onChange={event => set('id', event.target.value)}
              />
            </label>

            <label className="next-filter">
              <span className="next-filter__label">Court name</span>
              <input
                className="next-input"
                value={draft.CourtName ?? ''}
                placeholder="Filter by court name"
                onChange={event => set('CourtName', event.target.value)}
              />
            </label>

            <label className="next-filter">
              <span className="next-filter__label">Week day</span>
              <select
                className="next-input"
                value={draft.WeekDay ?? ''}
                onChange={event => set('WeekDay', event.target.value)}
              >
                <option value="">Any</option>
                {mappedWeekDays.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>

            <LookupSelect
              label="Facility"
              value={draft.FacilityId}
              onChange={value => set('FacilityId', value)}
              loadOptions={key => lookupFacility(api, key)}
              placeholder="Name or id"
            />

            {/* Type 2 is a membership, type 1 a product — same endpoint, and the
                only thing that separates these two filters. */}
            <LookupSelect
              label="Membership"
              value={draft.MembershipId}
              onChange={value => set('MembershipId', value)}
              loadOptions={key => lookupMembership(api, key)}
              placeholder="Name or id"
            />

            <LookupSelect
              label="Product"
              value={draft.ProductId}
              onChange={value => set('ProductId', value)}
              loadOptions={key => lookupBookableProduct(api, key)}
              placeholder="Name or id"
            />
          </div>

          <div className="next-filters__row">
            <div className="next-filters__buttons">
              <button type="submit" className="next-btn next-btn--primary">Search</button>
              <button
                type="button"
                className="next-btn next-btn--quiet"
                onClick={() => { setDraft({}); apply({}); }}
              >
                Clear
              </button>
            </div>
          </div>
        </form>
      </section>

      <ActiveFilters
        fields={FILTER_FIELDS}
        query={query}
        onChange={next => { setDraft(next); apply(next); }}
      />

      <ListTools
        screenPath="/next/seasonal-courts"
        search={location.search}
        columns={EXPORT_COLUMNS}
        rows={rows}
        exportName="seasonal-courts"
        perPage={perPage}
        // A copy of the filters, not the object itself — `searchCourts` mutates
        // what it is given.
        fetchPage={next => api.seasonalcourts
          .searchCourts({ filters: { ...query }, skipPages: next, resultsCount: perPage })
          .then(data => (Array.isArray(data) ? data : (data?.Courts ?? [])))}
      />

      {failed ? (
        <div className="next-note next-note--error">
          Could not load the seasonal courts.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      ) : (
        <section className="next-card">
          <div className="next-table-wrap">
            <table className="next-table next-table--wide next-table--stack">
              <thead>
                <tr>
                  <th>Id</th>
                  <th>Facility</th>
                  <th>Week day</th>
                  <th>Time (Copenhagen)</th>
                  <th>Court</th>
                  <th>Season start</th>
                  <th>Season end</th>
                  <th>Booked until</th>
                </tr>
              </thead>
              <tbody>
                {rows === null && (
                  <tr><td colSpan={8} className="next-muted next-table__note">Loading…</td></tr>
                )}
                {rows !== null && rows.length === 0 && (
                  <EmptyRow
                    columns={8}
                    noun="seasonal court"
                    filtered={filtered}
                    onClear={() => { setDraft({}); apply({}); }}
                  />
                )}
                {(rows ?? []).map(row => (
                  <tr key={row.Id}>
                    <td data-label=""><Guid value={row.Id} to={`/next/seasonal-court/${row.Id}`} /></td>
                    <td className="next-stack__main" data-label="Facility">{row.FacilityName || '—'}</td>
                    <td className="next-nowrap" data-label="Week day">{weekdaysReverse[row.WeekDay] || '—'}</td>
                    <td className="next-nowrap" data-label="Time">{fmtTime(row.Time)}</td>
                    <td data-label="Court">{row.CourtName || '—'}</td>
                    <td className="next-nowrap next-muted" data-label="Season start">{fmtDay(row.SeasonStart)}</td>
                    <td className="next-nowrap next-muted" data-label="Season end">{fmtDay(row.SeasonEnd)}</td>
                    <td className="next-nowrap" data-label="Booked until">{fmtDay(row.BookedUntil)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager
            perPage={perPage}
            onPerPageChange={size => apply({ ...query, resultsPerPage: size })}
            page={page}
            hasNext={(rows ?? []).length >= perPage}
            onChange={next => apply({ ...query, page: next }, { resetPage: false })}
          />
        </section>
      )}
    </div>
  );
};

SeasonalCourtsInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const SeasonalCourts = withApi(SeasonalCourtsInternal);

SeasonalCourts.requiresLogin = true;

export default SeasonalCourts;
